// File: client/src/variant/deck.js
// Deterministic deck generation, shuffling, and drawing logic.
//
// The card catalog is NOT imported here — it is fetched from the server
// (GET /api/games/cards) and passed in, so the DB remains the single source of
// truth for card data (no client-side duplication). generateDeck stays pure.

// The seeded PRNG now lives in the shared platform module (used by UNO + Randomizer).
// Imported for internal use and re-exported for backwards compatibility.
import { createPRNG } from '../platform/prng';
export { createPRNG };

/**
 * Shuffles an array in place (purely cloned) using the provided PRNG.
 * @param {Array} array
 * @param {function} prng
 * @returns {Array} shuffled copy of the array
 */
export function shuffle(array, prng) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(prng() * (i + 1));
    const temp = shuffled[i];
    shuffled[i] = shuffled[j];
    shuffled[j] = temp;
  }
  return shuffled;
}

// Number of copies of each card, by rarity.
const RARITY_COUNTS = { common: 4, uncommon: 2, rare: 1 };

/**
 * Generates a full game deck of cards, weighted by rarity and shuffled with a seed.
 *   - Common: 4 copies · Uncommon: 2 · Rare: 1
 * @param {number} seed
 * @param {Array} catalog card definitions (fetched from the server). Each entry
 *   must have card_id, rarity, and effect_json.
 * @returns {Array} List of card instances, each with a unique instance_id
 */
export function generateDeck(seed = 12345, catalog = []) {
  const rawDeck = [];
  let instanceCounter = 0;

  for (const cardDef of catalog) {
    const count = RARITY_COUNTS[cardDef.rarity] || 0;
    for (let i = 0; i < count; i++) {
      rawDeck.push({
        ...cardDef,
        // Unique ID for this card instance in the deck/hand
        instance_id: `${cardDef.card_id}_${instanceCounter++}`,
      });
    }
  }

  const prng = createPRNG(seed);
  return shuffle(rawDeck, prng);
}

/**
 * Pure helper to draw a card from a deck into a player's hand.
 * Respects maxHandSize. If hand is already full, returns the deck/hand unchanged.
 * @param {Array} deck
 * @param {Array} hand
 * @param {number} maxHandSize
 * @returns {{ deck: Array, hand: Array, drawnCard: object|null }}
 */
export function drawCard(deck, hand, maxHandSize = 5) {
  if (hand.length >= maxHandSize) {
    return { deck, hand, drawnCard: null };
  }
  if (deck.length === 0) {
    return { deck, hand, drawnCard: null };
  }
  const nextDeck = [...deck];
  const drawnCard = nextDeck.shift();
  const nextHand = [...hand, drawnCard];
  return { deck: nextDeck, hand: nextHand, drawnCard };
}
